/**
 * CodePicker — pick several items from a {code,label} vocabulary (languages, countries).
 *
 * Shows the selection as removable chips, a type-to-filter input and the first matches
 * as clickable suggestions. Stores CODES only (ISO 639-1 / ISO 3166-1), labels are display.
 * Shared by onboarding and Settings → Marketplace so both write the same values.
 */

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 8px;
`;

const Chip = styled.button`
  border: 1px solid ${p => (p.$selected ? '#2563eb' : 'rgba(255,255,255,0.18)')};
  background: ${p => (p.$selected ? '#2563eb' : 'rgba(255,255,255,0.04)')};
  color: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
  &:hover { border-color: #2563eb; }
  span.x { opacity: 0.7; font-size: 12px; }
`;

const Filter = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #2563eb; }
  &::placeholder { color: rgba(255,255,255,0.5); }
`;

const Hint = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
`;

export default function CodePicker({
  options = [],
  value = [],
  onChange,
  placeholder = 'Type to search…',
  quickPicks = 8,
  max = 50,
  emptyHint,
}) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(value), [value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = options.filter(o => !selected.has(o.code));
    if (!q) return pool.slice(0, quickPicks);
    return pool
      .filter(o => o.label.toLowerCase().includes(q) || o.code.toLowerCase() === q)
      .slice(0, 12);
  }, [options, selected, query, quickPicks]);

  const add = (code) => {
    if (selected.has(code) || value.length >= max) return;
    onChange([...value, code]);
    setQuery('');
  };
  const remove = (code) => onChange(value.filter(c => c !== code));
  const labelOf = (code) => options.find(o => o.code === code)?.label || code;

  return (
    <Wrap>
      {value.length > 0 ? (
        <Chips>
          {value.map(code => (
            <Chip type="button" key={code} $selected onClick={() => remove(code)} title="Remove">
              {labelOf(code)} <span className="x">✕</span>
            </Chip>
          ))}
        </Chips>
      ) : (emptyHint ? <Hint>{emptyHint}</Hint> : null)}
      <Filter
        value={query}
        placeholder={placeholder}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (matches[0]) add(matches[0].code);
          }
        }}
      />
      {matches.length > 0 && (
        <Chips>
          {matches.map(o => (
            <Chip type="button" key={o.code} onClick={() => add(o.code)}>+ {o.label}</Chip>
          ))}
        </Chips>
      )}
    </Wrap>
  );
}
